import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import Avatar from '../components/common/Avatar';
import Card from '../components/common/Card';
import TerritoryMap from '../components/TerritoryMap';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const EditTeamMemberPage = () => {
  const {
    API, activeTab, disconnectTeamMemberCalendar, editTeamMemberForm, editTeamMemberTab,
    editingTeamMember, existingTeamMemberAvatarUrl, packages, permissions, products, setActiveTab,
    setEditTeamMemberForm, setEditTeamMemberTab, setEditingTeamMember, setEditingTeamMemberId,
    setExistingTeamMemberAvatarUrl, setSelectedMemberForCalendar, setShowCalendarModal,
    setShowEditTeamMember, setTeamMemberAvatarFile, setTeamMemberAvatarPreview, setTeamMembers,
    teamMemberAvatarPreview, updateTeamMember,
  } = useDashboard();

  return (
    <div className="max-w-7xl mx-auto space-y-4 px-6 md:px-8 lg:px-12">
      {/* Back Button */}
      <button
        onClick={() => {
          setActiveTab('team');
          setEditingTeamMember(null);
          setEditingTeamMemberId(null);
        }}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Team
      </button>

      {/* Page Card */}
      <div className="clara-card rounded-lg overflow-hidden">
        <div className="px-8 py-4 bg-gray-700">
          <h2 className="text-xl font-semibold text-white">Edit Team Member</h2>
        </div>
        
        {/* Team Member Info Bar */}
        <div className="px-8 py-4 bg-gray-50 border-b">
          <div className="flex items-center gap-4">
            {(existingTeamMemberAvatarUrl || teamMemberAvatarPreview) ? (
              <img 
                src={teamMemberAvatarPreview || existingTeamMemberAvatarUrl}
                alt={editTeamMemberForm.name}
                className="w-16 h-16 rounded-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  setExistingTeamMemberAvatarUrl(null);
                }}
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-400 font-semibold text-xl">
                {editTeamMemberForm.name ? editTeamMemberForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
              </div>
            )}
            <div>
              <p className="text-lg font-semibold text-gray-900">{editTeamMemberForm.name || 'Team Member'}</p>
              <p className="text-sm text-gray-600">{editTeamMemberForm.email} • {editTeamMemberForm.role}</p>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation - Settings Page Style */}
        <div className="px-8 py-4 border-b">
          <div className="flex space-x-2">
            <button
              onClick={() => setEditTeamMemberTab('general')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'general' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'general' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
            >
              General
            </button>
            <button
              onClick={() => setEditTeamMemberTab('schedule')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'schedule' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'schedule' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
            >
              Schedule
            </button>
            <button
              onClick={() => setEditTeamMemberTab('territory')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'territory' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'territory' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
            >
              Territory
            </button>
            <button
              onClick={() => setEditTeamMemberTab('services')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'services' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'services' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
            >
              Services
            </button>
            <button
              onClick={() => setEditTeamMemberTab('permissions')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'permissions' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'permissions' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
            >
              Permissions
            </button>
            <button
              onClick={() => setEditTeamMemberTab('payroll')}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                editTeamMemberTab === 'payroll' ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
              }`}
              style={{
                color: editTeamMemberTab === 'payroll' ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
              }}
              data-testid="team-member-payroll-tab"
            >
              Payroll
            </button>
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="p-8 max-h-[calc(100vh-400px)] overflow-y-auto">
          {editTeamMemberTab === 'general' && (
          <div className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex items-center gap-6">
              {teamMemberAvatarPreview ? (
                <img
                  src={teamMemberAvatarPreview}
                  alt="New avatar preview"
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : existingTeamMemberAvatarUrl ? (
                <img
                  src={existingTeamMemberAvatarUrl}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover"
                  onError={(e) => { 
                    e.target.onerror = null;
                    setExistingTeamMemberAvatarUrl(null);
                  }}
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xl">
                  {editTeamMemberForm.name ? editTeamMemberForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
                </div>
              )}
              <div>
                <input
                  type="file"
                  id="edit-team-avatar-upload-page"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      setTeamMemberAvatarFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setTeamMemberAvatarPreview(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="edit-team-avatar-upload-page"
                  className="clara-btn-secondary px-4 py-2 cursor-pointer inline-block"
                >
                  {editTeamMemberForm.avatar || teamMemberAvatarPreview ? 'Change Photo' : 'Upload Photo'}
                </label>
                {teamMemberAvatarPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setTeamMemberAvatarFile(null);
                      setTeamMemberAvatarPreview(null);
                    }}
                    className="ml-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                )}
                <p className="text-xs mt-1" style={{ color: 'var(--clara-text-muted)' }}>
                  Max file size: 5MB (JPG, PNG, GIF)
                </p>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Name</label>
              <input
                type="text"
                value={editTeamMemberForm.name}
                onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, name: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Email</label>
              <input
                type="email"
                value={editTeamMemberForm.email}
                onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, email: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Role</label>
              <select
                value={editTeamMemberForm.role}
                onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, role: e.target.value})}
                className="clara-input w-full"
              >
                <option value="Agent">Agent</option>
                <option value="Photographer">Photographer</option>
                <option value="Videographer">Videographer</option>
                <option value="Photo Editor">Photo Editor</option>
                <option value="Video Editor">Video Editor</option>
                <option value="Manager">Manager</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Phone</label>
              <input
                type="tel"
                value={editTeamMemberForm.phone}
                onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, phone: e.target.value})}
                className="clara-input w-full"
              />
            </div>
          </div>

          {/* Account Status */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="block clara-body-sm font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>Account Status</label>
                <div className="flex items-center gap-2">
                  {editingTeamMember?.invitation_status === 'accepted' || editingTeamMember?.user_id ? (
                    <>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        ✓ Active
                      </span>
                      <span className="text-sm text-gray-600">
                        Team member has accepted invitation and has an active account
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-300 rounded-full text-sm font-medium">
                        ⏳ Invited
                      </span>
                      <span className="text-sm text-gray-600">
                        {editingTeamMember?.invitation_status === 'pending' 
                          ? 'Waiting for team member to accept invitation' 
                          : 'Legacy team member - send invitation to activate'}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {!(editingTeamMember?.invitation_status === 'accepted' || editingTeamMember?.user_id) && (
                <button
                  onClick={async () => {
                    try {
                      // Always use resend endpoint - it handles both new invitations and resends
                      await axios.post(`${API}/team/resend-invitation/${editingTeamMember.id}`);
                      alert('Invitation sent successfully! Check your email.');
                      
                      // Refresh team members to show updated status
                      const response = await axios.get(`${API}/settings/team-members`);
                      setTeamMembers(response.data);
                      
                      // Update the editing team member state
                      const updatedMember = response.data.find(m => m.id === editingTeamMember.id);
                      if (updatedMember) {
                        setEditingTeamMember(updatedMember);
                      }
                    } catch (error) {
                      alert('Failed to send invitation: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                >
                  {editingTeamMember?.invitation_status === 'pending' ? 'Resend Invitation' : 'Send Invitation'}
                </button>
              )}
            </div>
          </div>

          </div>
          )}

          {/* Permissions Tab */}
          {editTeamMemberTab === 'permissions' && (
          <div className="space-y-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="block clara-body font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>
              Access Permissions
            </label>
            <p className="text-xs text-gray-500 mb-4">Control what this team member can view and modify</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Dashboard */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">📊 Dashboard</span>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editTeamMemberForm.permissions?.dashboard?.view !== false}
                    onChange={(e) => setEditTeamMemberForm({
                      ...editTeamMemberForm,
                      permissions: {
                        ...editTeamMemberForm.permissions,
                        dashboard: { view: e.target.checked }
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">View Dashboard</span>
                </label>
              </div>

              {/* Clients */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">👥 Clients</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.clients?.view !== false}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          clients: { ...editTeamMemberForm.permissions?.clients, view: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Clients</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.clients?.create === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          clients: { ...editTeamMemberForm.permissions?.clients, create: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Create Clients</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.clients?.edit === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          clients: { ...editTeamMemberForm.permissions?.clients, edit: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Edit Clients</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.clients?.delete === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          clients: { ...editTeamMemberForm.permissions?.clients, delete: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Delete Clients</span>
                  </label>
                </div>
              </div>

              {/* Appointments */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">📅 Appointments</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.appointments?.view !== false}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          appointments: { ...editTeamMemberForm.permissions?.appointments, view: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Appointments</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.appointments?.view_all === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          appointments: { ...editTeamMemberForm.permissions?.appointments, view_all: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View All Appointments (vs. only assigned)</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.appointments?.create === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          appointments: { ...editTeamMemberForm.permissions?.appointments, create: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Create Appointments</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.appointments?.edit === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          appointments: { ...editTeamMemberForm.permissions?.appointments, edit: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Edit Appointments</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.appointments?.delete === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          appointments: { ...editTeamMemberForm.permissions?.appointments, delete: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Delete Appointments</span>
                  </label>
                </div>
              </div>

              {/* Listings */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">🏠 Listings</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.listings?.view !== false}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          listings: { ...editTeamMemberForm.permissions?.listings, view: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Listings</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.listings?.edit === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          listings: { ...editTeamMemberForm.permissions?.listings, edit: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Edit Listings</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.listings?.manage_media === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          listings: { ...editTeamMemberForm.permissions?.listings, manage_media: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Manage Media</span>
                  </label>
                </div>
              </div>

              {/* Team Management */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">👤 Team Management</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.team?.view === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          team: { ...editTeamMemberForm.permissions?.team, view: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Team Members</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.team?.manage === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          team: { ...editTeamMemberForm.permissions?.team, manage: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Add/Edit/Remove Team Members</span>
                  </label>
                </div>
              </div>

              {/* Settings */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">⚙️ Settings</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.settings?.view_products === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          settings: { ...editTeamMemberForm.permissions?.settings, view_products: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Products/Services</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.settings?.manage_products === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          settings: { ...editTeamMemberForm.permissions?.settings, manage_products: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Manage Products/Services</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.settings?.manage_business === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          settings: { ...editTeamMemberForm.permissions?.settings, manage_business: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Manage Business Settings</span>
                  </label>
                </div>
              </div>

              {/* Tasks */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">✓ Tasks</span>
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.tasks?.view !== false}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          tasks: { ...editTeamMemberForm.permissions?.tasks, view: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View Tasks</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.tasks?.view_all === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          tasks: { ...editTeamMemberForm.permissions?.tasks, view_all: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">View All Tasks (vs. only assigned)</span>
                  </label>
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.permissions?.tasks?.manage === true}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          ...editTeamMemberForm.permissions,
                          tasks: { ...editTeamMemberForm.permissions?.tasks, manage: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">Create/Edit/Delete Tasks</span>
                  </label>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-medium text-gray-500 mb-2">Quick Presets:</p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          dashboard: { view: true },
                          clients: { view: true, create: true, edit: true, delete: true },
                          appointments: { view: true, view_all: true, create: true, edit: true, delete: true },
                          listings: { view: true, edit: true, manage_media: true },
                          team: { view: true, manage: true },
                          settings: { view_products: true, manage_products: true, manage_business: true },
                          tasks: { view: true, view_all: true, manage: true }
                        }
                      });
                    }}
                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                  >
                    👑 Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          dashboard: { view: true },
                          clients: { view: true, create: true, edit: true, delete: false },
                          appointments: { view: true, view_all: true, create: true, edit: true, delete: false },
                          listings: { view: true, edit: true, manage_media: true },
                          team: { view: true, manage: false },
                          settings: { view_products: true, manage_products: false, manage_business: false },
                          tasks: { view: true, view_all: true, manage: true }
                        }
                      });
                    }}
                    className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                  >
                    📊 Manager
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          dashboard: { view: true },
                          clients: { view: true, create: false, edit: false, delete: false },
                          appointments: { view: true, view_all: false, create: false, edit: false, delete: false },
                          listings: { view: true, edit: false, manage_media: false },
                          team: { view: false, manage: false },
                          settings: { view_products: true, manage_products: false, manage_business: false },
                          tasks: { view: true, view_all: false, manage: false }
                        }
                      });
                    }}
                    className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                  >
                    👁️ Viewer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        permissions: {
                          dashboard: { view: true },
                          clients: { view: false, create: false, edit: false, delete: false },
                          appointments: { view: true, view_all: false, create: false, edit: false, delete: false },
                          listings: { view: false, edit: false, manage_media: false },
                          team: { view: false, manage: false },
                          settings: { view_products: false, manage_products: false, manage_business: false },
                          tasks: { view: true, view_all: false, manage: true }
                        }
                      });
                    }}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    📸 Photographer
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
          )}

          {/* Services Tab */}
          {editTeamMemberTab === 'services' && (
          <div className="space-y-6">
          {/* Assigned Packages */}
          <div>
            <h4 className="clara-body font-medium mb-4 text-gray-900 dark:text-white">Assigned Packages</h4>
            {packages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {packages.filter(p => p.is_active !== false).map((pkg) => (
                  <label key={pkg.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.assigned_packages?.includes(pkg.id)}
                      onChange={(e) => {
                        const currentPackages = editTeamMemberForm.assigned_packages || [];
                        const updatedPackages = e.target.checked
                          ? [...currentPackages, pkg.id]
                          : currentPackages.filter(id => id !== pkg.id);
                        setEditTeamMemberForm({...editTeamMemberForm, assigned_packages: updatedPackages});
                      }}
                      className="mr-2"
                    />
                    <span className="clara-body-sm">{pkg.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                No packages available. Create packages first to assign them to team members.
              </p>
            )}
          </div>

          {/* Assigned Services */}
          <div>
            <h4 className="clara-body font-medium mb-4 text-gray-900 dark:text-white">Assigned Services</h4>
            {products.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {products.filter(p => p.is_active !== false).map((product) => (
                  <label key={product.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editTeamMemberForm.assigned_products.includes(product.id)}
                      onChange={(e) => {
                        const updatedProducts = e.target.checked
                          ? [...editTeamMemberForm.assigned_products, product.id]
                          : editTeamMemberForm.assigned_products.filter(id => id !== product.id);
                        setEditTeamMemberForm({...editTeamMemberForm, assigned_products: updatedProducts});
                      }}
                      className="mr-2"
                    />
                    <span className="clara-body-sm">{product.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                No services available. Add services first to assign them to team members.
              </p>
            )}
          </div>
          </div>
          )}

          {/* Territory Tab */}
          {editTeamMemberTab === 'territory' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Home Base & Mileage */}
            <div className="space-y-6">
              {/* Home Base Address */}
              <div className="p-6 rounded-xl bg-gray-50 dark:bg-gray-800">
                <h4 className="font-medium mb-4 text-gray-900 dark:text-white">
                  🏠 Home Base Address
                </h4>
                <p className="text-sm mb-4 text-gray-600 dark:text-gray-300">
                  Enter the address where this team member is based. This will be used to calculate service territory.
                </p>
                <input
                  type="text"
                  placeholder="Enter home base address..."
                  value={editTeamMemberForm.home_base_address || ''}
                  onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, home_base_address: e.target.value})}
                  className="clara-input w-full"
                />
              </div>

              {/* Territory Info */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-700">
                <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">⚠️ How territory filtering works</p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  When a client enters their property address on the booking form, the system calculates the distance from each photographer's home base. Only photographers whose territory covers the property location will be shown as available for scheduling.
                </p>
              </div>

              {/* Mileage Fee Tiers */}
              <div className="p-6 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      💰 Mileage Fee Tiers
                    </h4>
                    <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">
                      Set distance-based fees for travel compensation.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const tiers = [...(editTeamMemberForm.mileage_tiers || [])];
                      const lastTier = tiers[tiers.length - 1];
                      const newMin = lastTier ? lastTier.max_miles : 0;
                      tiers.push({ min_miles: newMin, max_miles: newMin + 25, fee: 0 });
                      setEditTeamMemberForm({...editTeamMemberForm, mileage_tiers: tiers});
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg transition-all duration-200 flex items-center gap-1 bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
                  >
                    <span>+</span> Add Tier
                  </button>
                </div>

                {(!editTeamMemberForm.mileage_tiers || editTeamMemberForm.mileage_tiers.length === 0) ? (
                  <div className="p-6 text-center border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No mileage tiers configured</p>
                    <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">Add tiers to charge distance-based fees</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {editTeamMemberForm.mileage_tiers.map((tier, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800">
                        <span className="text-sm text-gray-700 dark:text-gray-200">From</span>
                        <input
                          type="number"
                          min="0"
                          value={tier.min_miles}
                          onChange={(e) => {
                            const tiers = [...editTeamMemberForm.mileage_tiers];
                            tiers[index] = {...tiers[index], min_miles: parseFloat(e.target.value) || 0};
                            setEditTeamMemberForm({...editTeamMemberForm, mileage_tiers: tiers});
                          }}
                          className="w-14 px-1 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-200">to</span>
                        <input
                          type="number"
                          min="0"
                          value={tier.max_miles}
                          onChange={(e) => {
                            const tiers = [...editTeamMemberForm.mileage_tiers];
                            tiers[index] = {...tiers[index], max_miles: parseFloat(e.target.value) || 0};
                            setEditTeamMemberForm({...editTeamMemberForm, mileage_tiers: tiers});
                          }}
                          className="w-14 px-1 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-300">mi</span>
                        <span className="text-sm text-gray-700 dark:text-gray-200">=</span>
                        <span className="text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.fee}
                          onChange={(e) => {
                            const tiers = [...editTeamMemberForm.mileage_tiers];
                            tiers[index] = {...tiers[index], fee: parseFloat(e.target.value) || 0};
                            setEditTeamMemberForm({...editTeamMemberForm, mileage_tiers: tiers});
                          }}
                          className="w-16 px-1 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          onClick={() => {
                            const tiers = editTeamMemberForm.mileage_tiers.filter((_, i) => i !== index);
                            setEditTeamMemberForm({...editTeamMemberForm, mileage_tiers: tiers});
                          }}
                          className="ml-auto p-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                          title="Remove tier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    
                    {/* Mileage Summary */}
                    <div className="mt-4 p-4 rounded-xl bg-gray-100 dark:bg-gray-700">
                      <p className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-200">📋 Fee Summary:</p>
                      <div className="space-y-1">
                        {editTeamMemberForm.mileage_tiers.map((tier, index) => (
                          <p key={index} className="text-sm text-gray-800 dark:text-gray-200">
                            {tier.min_miles}-{tier.max_miles} miles: {tier.fee === 0 ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">Free</span>
                            ) : (
                              <span className="text-blue-600 dark:text-blue-400 font-medium">${tier.fee.toFixed(2)}</span>
                            )}
                          </p>
                        ))}
                        {editTeamMemberForm.mileage_tiers.length > 0 && (
                          <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
                            Beyond {editTeamMemberForm.mileage_tiers[editTeamMemberForm.mileage_tiers.length - 1]?.max_miles || 0} miles: Outside service area
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Service Territory + Map */}
            <div className="space-y-6">
              {/* Service Territory Radius */}
              <div className="p-6 rounded-xl bg-gray-50 dark:bg-gray-800">
                <h4 className="font-medium mb-4 text-gray-900 dark:text-white">
                  📍 Service Territory
                </h4>
                <p className="text-sm mb-4 text-gray-600 dark:text-gray-300">
                  Set the maximum distance this team member will travel for appointments.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Service Radius (miles)</label>
                    <input
                      type="range"
                      min="5"
                      max="150"
                      step="5"
                      value={editTeamMemberForm.territory_radius_miles || 50}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, territory_radius_miles: parseFloat(e.target.value) || 50})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs mt-1 text-gray-500 dark:text-gray-400">
                      <span>5 mi</span>
                      <span className="font-semibold text-base text-blue-600 dark:text-blue-400">{editTeamMemberForm.territory_radius_miles || 50} miles</span>
                      <span>150 mi</span>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      This team member will be available for bookings within a <strong className="text-blue-900 dark:text-blue-100">{editTeamMemberForm.territory_radius_miles || 50} mile</strong> radius from their home base.
                    </p>
                    {editTeamMemberForm.home_base_address && (
                      <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">📍 From: {editTeamMemberForm.home_base_address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="p-6 rounded-xl bg-gray-50 dark:bg-gray-800" style={{ minHeight: '350px' }}>
                <h4 className="font-medium mb-4 text-gray-900 dark:text-white">
                  🗺️ Coverage Area Preview
                </h4>
                <TerritoryMap 
                  address={editTeamMemberForm.home_base_address}
                  radiusMiles={editTeamMemberForm.territory_radius_miles || 50}
                />
              </div>
            </div>
          </div>
          )}

          {/* Schedule Tab */}
          {editTeamMemberTab === 'schedule' && (
          <div className="space-y-6">
          {/* Working Hours */}
          <div>
            <h4 className="clara-body font-medium mb-4" style={{ color: 'var(--clara-primary)' }}>Working Hours</h4>
            <div className="space-y-3">
              {Object.entries(editTeamMemberForm.working_hours).map(([day, hours]) => (
                <div key={day} className="grid grid-cols-4 gap-3 items-center">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={hours.enabled}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        working_hours: {
                          ...editTeamMemberForm.working_hours,
                          [day]: { ...hours, enabled: e.target.checked }
                        }
                      })}
                      className="mr-2"
                    />
                    <span className="clara-body-sm capitalize">{day}</span>
                  </div>
                  <div>
                    <input
                      type="time"
                      value={hours.start}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        working_hours: {
                          ...editTeamMemberForm.working_hours,
                          [day]: { ...hours, start: e.target.value }
                        }
                      })}
                      disabled={!hours.enabled}
                      className="clara-input w-full text-sm"
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      value={hours.end}
                      onChange={(e) => setEditTeamMemberForm({
                        ...editTeamMemberForm,
                        working_hours: {
                          ...editTeamMemberForm.working_hours,
                          [day]: { ...hours, end: e.target.value }
                        }
                      })}
                      disabled={!hours.enabled}
                      className="clara-input w-full text-sm"
                    />
                  </div>
                  <div className="text-xs" style={{ color: 'var(--clara-medium-gray)' }}>
                    {hours.enabled ? `${hours.start} - ${hours.end}` : 'Off'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Google Calendar Integration */}
          <div>
            <h4 className="clara-body font-medium mb-4" style={{ color: 'var(--clara-primary)' }}>Google Calendar Integration</h4>
            {editingTeamMember && editingTeamMember.calendar_integration && editingTeamMember.calendar_integration.is_active ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100">Connected</p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {editingTeamMember.calendar_integration.connected_email || 'Google Calendar'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => disconnectTeamMemberCalendar(editingTeamMember.id)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Not Connected</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Connect to sync availability and events</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMemberForCalendar(editingTeamMember);
                      setShowEditTeamMember(false);
                      setShowCalendarModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                  >
                    Connect Calendar
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
          )}

          {/* Payroll Tab */}
          {editTeamMemberTab === 'payroll' && (
            <PayrollOverridesTab
              API={API}
              teamMemberId={editingTeamMember?.id}
              assignedProductIds={editTeamMemberForm.assigned_products || []}
              assignedPackageIds={editTeamMemberForm.assigned_packages || []}
            />
          )}

        </div>

        {/* Footer with buttons */}
        <div className="px-8 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            onClick={() => {
              setActiveTab('team');
              setEditingTeamMember(null);
              setEditingTeamMemberId(null);
            }}
            className="clara-btn-secondary px-6 py-2"
          >
            Cancel
          </button>
          <button
            onClick={updateTeamMember}
            className="clara-btn-primary px-6 py-2"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const PayrollOverridesTab = ({ API, teamMemberId, assignedProductIds = [], assignedPackageIds = [] }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!teamMemberId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/payroll/overrides/${teamMemberId}`);
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      } catch (_e) { /* noop */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [API, teamMemberId]);

  const filteredRows = React.useMemo(() => {
    const assignedProductSet = new Set(assignedProductIds);
    const assignedPackageSet = new Set(assignedPackageIds);
    return rows.filter(r => {
      if (r.entity_type === 'product') return assignedProductSet.has(r.product_id);
      if (r.entity_type === 'package') return assignedPackageSet.has(r.product_id);
      // add-ons aren't part of the Services tab assignment — always show
      return true;
    });
  }, [rows, assignedProductIds, assignedPackageIds]);

  const handleChange = async (productId, raw, tierKey) => {
    setSavingId(`${productId}::${tierKey || 'base'}`);
    const amount = raw === '' || raw === null ? null : Number(raw);
    try {
      await axios.put(`${API}/payroll/overrides/${teamMemberId}/${productId}`, { amount, tier_key: tierKey });
      setRows(prev => prev.map(r => {
        if (r.product_id !== productId) return r;
        if (tierKey == null) return { ...r, override_amount: amount };
        return { ...r, tiers: (r.tiers || []).map(t => t.tier_key === tierKey ? { ...t, override_amount: amount } : t) };
      }));
    } catch (_e) { /* noop */ }
    setSavingId(null);
  };

  if (!teamMemberId) return <p className="text-sm text-gray-500">Save this team member first to manage payroll overrides.</p>;
  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;

  const effectiveAmount = (r) => (r.override_amount !== null && r.override_amount !== undefined) ? r.override_amount : r.default_amount;

  return (
    <div className="space-y-4" data-testid="payroll-overrides-tab">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Per-Product Payouts</h3>
        <p className="text-sm text-gray-600 mt-1">Showing only services this team member is assigned to (in the Services tab). Leave the override blank to use the business default. Products with square-footage tiers can have a per-tier override.</p>
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Product / Variant</th>
              <th className="px-3 py-2 text-right">Sells For</th>
              <th className="px-3 py-2 text-right">Default Payout</th>
              <th className="px-3 py-2 text-right">Override</th>
              <th className="px-3 py-2 text-right">Effective</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan="5" className="px-3 py-6 text-center text-gray-500">
                {rows.length === 0
                  ? 'No products yet'
                  : 'No services assigned. Assign products in the Services tab to set payouts.'}
              </td></tr>
            ) : filteredRows.map(r => {
              const baseEffective = effectiveAmount(r);
              const hasTiers = (r.tiers || []).length > 0;
              return (
                <React.Fragment key={r.product_id}>
                  <tr className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-900 font-medium">
                      {r.product_name}
                      {r.entity_type === 'addon' && <span className="ml-2 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">Add-on</span>}
                      {r.entity_type === 'package' && <span className="ml-2 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Package</span>}
                      {hasTiers && <span className="ml-2 text-[10px] text-gray-400 font-normal">(base — no tier match)</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">${Number(r.product_price || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">${Number(r.default_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <span className="text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={r.override_amount ?? ''}
                          placeholder="—"
                          onBlur={(e) => handleChange(r.product_id, e.target.value, null)}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                          data-testid={`override-input-${r.product_id}`}
                        />
                        {savingId === `${r.product_id}::base` && <span className="text-xs text-gray-400">…</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">${Number(baseEffective || 0).toFixed(2)}</td>
                  </tr>
                  {(r.tiers || []).map(t => {
                    const tEffective = (t.override_amount !== null && t.override_amount !== undefined) ? t.override_amount : t.default_amount;
                    return (
                      <tr key={t.tier_key} className="border-t border-gray-50 bg-gray-50/40">
                        <td className="px-3 py-2 text-gray-700 pl-10">
                          <div className="flex items-center gap-1.5">
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            <span>{t.min_sqft}-{t.max_sqft} sqft</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">${Number(t.price || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">${Number(t.default_amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <span className="text-gray-500">$</span>
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={t.override_amount ?? ''}
                              placeholder="—"
                              onBlur={(e) => handleChange(r.product_id, e.target.value, t.tier_key)}
                              className="w-24 px-2 py-1 border border-gray-200 rounded text-sm text-right"
                              data-testid={`override-input-${r.product_id}-${t.tier_key}`}
                            />
                            {savingId === `${r.product_id}::${t.tier_key}` && <span className="text-xs text-gray-400">…</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900">${Number(tEffective || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EditTeamMemberPage;